import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Container, Button, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import _ from 'lodash';

import { useUserContext } from 'providers/UserProvider';
import SubtleBadge from 'components/common/SubtleBadge';
import PrintSizeDropdown from 'components/common/PrintSizeDropdown';
import GRNLineTable from 'components/purchase-order/put-away/PutawayDetails/GRNLineTable';
import GRNLinesEditModal from 'components/purchase-order/put-away/PutawayDetails/GRNLinesEditModal';

import paths from 'routes/paths';
import { putAwayApi, grnApi } from 'api';
import { formatStatus } from 'utils/valueFormatters';
import {
  printPutAwayInstructionPdf,
  getPutAwayInstructionErrorMessage,
} from 'utils/putAwayInstructionPrint';
import { PRINT_PAGE_SIZE_STORAGE_KEYS } from 'constants/printPageSize';

const PutawayDetail = () => {
  const navigate = useNavigate();
  const { putawayId } = useParams();
  const [searchParams] = useSearchParams();

  const { selectedTenant, selectedWarehouse, warehouse_permissions } =
    useUserContext();

  const currentPermission = warehouse_permissions?.find(
    p => p.warehouse?.id === selectedWarehouse?.id,
  );
  const canCompletePutaway = currentPermission?.put_away >= 2;
  const tenant = selectedTenant?.domains?.[0]?.domain;
  const warehouse = selectedWarehouse?.domain;

  const isCompleted = searchParams.get('completed') === 'true';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [grnLines, setGrnLines] = useState([]);

  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedGrnId, setSelectedGrnId] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);

  const fetchGRNLines = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await putAwayApi.getAllPutawayGRNLines(putawayId);
      setGrnLines(response);
      setError(null);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || err.message || 'An error occurred';
      setError(errorMessage);

      toast.error('Fail to load GRN lines');
    } finally {
      setLoading(false);
    }
  }, [putawayId]);

  useEffect(() => {
    fetchGRNLines();
  }, [fetchGRNLines]);

  const groupedGRNLines = useMemo(() => {
    const grouped = _.groupBy(grnLines, 'grn_id');

    return Object.entries(grouped).map(([grnId, items]) => ({
      grnId: parseInt(grnId),
      grn_type: items[0]?.grn_type, // Get grn_type from the first item
      items: items.map(item => ({
        ...item,
      })),
      hasAllShelfCodes: items.every(
        item => item.shelf_code && item.shelf_code.trim() !== '',
      ),
    }));
  }, [grnLines]);

  const hasAllShelfCodes = useMemo(() => {
    const linesToCheck = grnLines.filter(
      line => line.inventory_type !== 'reject' && line.quantity > 0,
    );

    return linesToCheck.every(
      line => line.shelf_code && line.shelf_code.trim() !== '',
    );
  }, [grnLines]);

  const handleEdit = (grnId, selectedRows) => {
    setSelectedGrnId(grnId);
    setSelectedItems(selectedRows);
    setShowEditModal(true);
  };

  const handleEditModalClose = () => {
    setShowEditModal(false);
    setSelectedItems([]);
    setSelectedGrnId(null);
  };

  const handleCompletePutaway = async () => {
    try {
      await putAwayApi.completePutaway(putawayId);
      toast.success('Putaway completed successfully');
      setTimeout(() => {
        navigate(paths.putAway.list(tenant, warehouse));
      }, 300);
    } catch (error) {
      toast.error('Fail to complete putaway');
    }
  };

  const handlePrintInstruction = async pageSize => {
    try {
      await printPutAwayInstructionPdf(putawayId, pageSize, {
        tenantName: selectedTenant?.name,
        warehouseName: selectedWarehouse?.name,
      });
    } catch (error) {
      toast.error(getPutAwayInstructionErrorMessage(error));
    }
  };

  const handleUpdateGRNLines = async updatedItems => {
    try {
      await grnApi.updateGRNLinesShelf(selectedGrnId, updatedItems);

      await fetchGRNLines();

      toast.success('GRN lines updated successfully');
    } catch (error) {
      toast.error('Fail to update GRN lines');
    }
  };

  return (
    <Container fluid className="p-0 d-flex flex-column gap-4">
      <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-2">
          <h5 className="mb-0">Putaway #{putawayId}</h5>

          <SubtleBadge pill bg={isCompleted ? 'success' : 'warning'}>
            {formatStatus(isCompleted ? 'complete' : 'pending')}
          </SubtleBadge>
        </div>

        {isCompleted && (
          <PrintSizeDropdown
            storageKey={PRINT_PAGE_SIZE_STORAGE_KEYS.PUT_AWAY_INSTRUCTION}
            title="Print put-away instruction"
            label="Print Instruction"
            onPrint={handlePrintInstruction}
          />
        )}

        {canCompletePutaway && !isCompleted && (
          <Button
            variant="primary"
            size="sm"
            onClick={handleCompletePutaway}
            disabled={loading || grnLines.length === 0 || !hasAllShelfCodes}
          >
            Complete Putaway
          </Button>
        )}
      </div>

      {!isCompleted && !hasAllShelfCodes && (
        <Alert variant="info" className="mb-0">
          A shelf must be set for every item before the putaway can be
          completed.
        </Alert>
      )}

      {loading ? (
        <div className="text-center">
          <div className="spinner-border" role="status" />
        </div>
      ) : error ? (
        <div className="text-center text-danger p-4">{error}</div>
      ) : groupedGRNLines.length === 0 ? (
        <div className="text-center p-4">No data available</div>
      ) : (
        groupedGRNLines.map(grnGroup => (
          <GRNLineTable
            key={grnGroup.grnId}
            grnData={grnGroup}
            onEdit={handleEdit}
            isCompleted={isCompleted}
            grnType={grnGroup.grn_type}
            hasAllShelfCodes={grnGroup.hasAllShelfCodes}
          />
        ))
      )}

      {!isCompleted && (
        <GRNLinesEditModal
          show={showEditModal}
          onHide={handleEditModalClose}
          selectedItems={selectedItems}
          onSave={handleUpdateGRNLines}
        />
      )}
    </Container>
  );
};

export default PutawayDetail;
